import { useState } from 'react';
import { Loader2, ChevronRight, AlertCircle } from 'lucide-react';
import type { ProviderAttempt } from '@/lib/types';
import JsonBlock from './JsonBlock';

interface ProviderAttemptsSectionProps {
  attempts: ProviderAttempt[] | undefined;
  isLoading: boolean;
  className?: string;
}

function maskToken(token: string | null | undefined): string {
  if (!token) return '—';
  if (token.length <= 4) return token;
  return `•••• ${token.slice(-4)}`;
}

function Ts({ ts }: { ts: string | null }) {
  // Display in the operator's locale for readability; expose the raw ISO via title for log correlation.
  if (!ts) return <>—</>;
  return <span title={ts}>{new Date(ts).toLocaleString()}</span>;
}

function statusStyle(attempt: ProviderAttempt): {
  border: string;
  accent: string;
  badge: string;
} {
  if (attempt.error) {
    return {
      border: 'border-red-500/30',
      accent: 'bg-red-500/5',
      badge: 'bg-red-500/20 text-red-400',
    };
  }
  switch (attempt.status) {
    case 'consumed':
      return {
        border: 'border-green-500/30',
        accent: 'bg-green-500/5',
        badge: 'bg-green-500/20 text-green-400',
      };
    case 'cancelled':
      return {
        border: 'border-red-500/30',
        accent: 'bg-red-500/5',
        badge: 'bg-red-500/20 text-red-400',
      };
    case 'callback_received':
      return {
        border: 'border-amber-500/30',
        accent: 'bg-amber-500/5',
        badge: 'bg-amber-500/20 text-amber-400',
      };
    case 'submitted':
    case 'created':
    default:
      return {
        border: 'border-border',
        accent: 'bg-muted/20',
        badge: 'bg-muted/60 text-muted-foreground',
      };
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-0.5">{label}</div>
      <div className="text-xs font-mono break-all text-foreground/90">{children}</div>
    </div>
  );
}

function AttemptCard({ attempt }: { attempt: ProviderAttempt }) {
  const [expanded, setExpanded] = useState(false);
  const { border, accent, badge } = statusStyle(attempt);

  const keyTs = attempt.consume_ts || attempt.callback_receive_ts || attempt.submit_ts || attempt.create_ts;

  return (
    <div className={`rounded-lg border ${border} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/30 ${accent}`}
        aria-expanded={expanded}
      >
        <ChevronRight
          className={`h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform ${
            expanded ? 'rotate-90' : ''
          }`}
        />
        <span className="font-medium text-sm flex-shrink-0">{attempt.provider_name}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide ${badge}`}>
          {attempt.status}
        </span>
        {attempt.error && <AlertCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />}
        <span className="text-xs font-mono text-muted-foreground/80 truncate flex-1 min-w-0" title={attempt.external_request_id ?? undefined}>
          {attempt.external_request_id || <span className="opacity-50">no request id</span>}
        </span>
        <span className="text-xs text-muted-foreground font-mono flex-shrink-0 tabular-nums">
          <Ts ts={keyTs} />
        </span>
      </button>

      {expanded && (
        <div className="border-t border-border/50 p-4 space-y-4 bg-background/40">
          <FieldGroup label="Identity">
            <Field label="id">{attempt.id}</Field>
            <Field label="job_id">{attempt.job_id}</Field>
            <Field label="assignment_id">{attempt.assignment_id}</Field>
            <Field label="callback_token">
              <span title="Token masked in UI — query DB for the full value">
                {maskToken(attempt.callback_token)}
              </span>
            </Field>
          </FieldGroup>

          <FieldGroup label="Timing">
            <Field label="create_ts"><Ts ts={attempt.create_ts} /></Field>
            <Field label="submit_ts"><Ts ts={attempt.submit_ts} /></Field>
            <Field label="callback_receive_ts"><Ts ts={attempt.callback_receive_ts} /></Field>
            <Field label="consume_ts"><Ts ts={attempt.consume_ts} /></Field>
            <Field label="update_ts"><Ts ts={attempt.update_ts} /></Field>
          </FieldGroup>

          {(attempt.submit_response || attempt.callback_payload || attempt.error) && (
            <div className="space-y-2 pt-1">
              <JsonBlock label="submit_response — outbound → provider" value={attempt.submit_response} />
              <JsonBlock label="callback_payload — provider → us" value={attempt.callback_payload} />
              {attempt.error && (
                <div className="border border-red-500/30 rounded bg-red-500/5 p-2">
                  <JsonBlock label="error" value={attempt.error} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
          {label}
        </div>
        <div className="flex-1 h-px bg-border/40" />
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">{children}</div>
    </div>
  );
}

export default function ProviderAttemptsSection({
  attempts,
  isLoading,
  className = '',
}: ProviderAttemptsSectionProps) {
  return (
    <div className={`space-y-3 ${className}`}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading provider attempts...
        </div>
      ) : attempts && attempts.length > 0 ? (
        <div className="space-y-2">
          {attempts.map((attempt) => (
            <AttemptCard key={attempt.id} attempt={attempt} />
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">No provider attempts recorded yet.</div>
      )}
    </div>
  );
}
